import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Save, RotateCcw, LogOut, ArrowLeft, Loader2, KeyRound, ShieldCheck, RefreshCw, Search, ChevronLeft, ChevronRight, Eraser, Download, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  fetchGeneralSettings,
  fetchDstSettings,
  updateGeneralSettings,
  updateDst,
  resetDefaults,
  changePassword,
  fetchAuditLogs,
  clearAuditLogs,
  downloadAuditExport,
  me,
  logout,
} from "@/lib/api";

export default function Admin() {
  const navigate = useNavigate();
  const [authChecking, setAuthChecking] = useState(true);
  const [generalSettings, setGeneralSettings] = useState({
    default_monthly_rate: 0.02,
    interest_formula: "simple",
    term_options: [6, 7, 8, 9, 10, 11, 12],
    pmt_rates: [],
    field_visibility: {
      show_loan_for_business: false,
      show_number_of_days: false,
      show_maturity_date: false,
      default_loan_for_business: "No",
    },
    allow_interest_rate_edit: true,
    audit_retention_days: 90,
    min_loan_amount: 200000,
    show_logo: true,
    show_amortization: false,
    disclosure_charges: {
      missed_payment_penalty: "",
      prepayment_charges: "",
      past_due_interest: "",
      past_due_penalty: "",
      attorneys_fees: "",
    },
    theme: "modern",
  });
  const [termOptionsInput, setTermOptionsInput] = useState("6, 7, 8, 9, 10, 11, 12");
  const [dst, setDst] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Password change
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [changingPwd, setChangingPwd] = useState(false);

  // Audit log
  const PAGE_SIZE = 25;
  const [auditLogs, setAuditLogs] = useState({ items: [], total: 0 });
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditFilter, setAuditFilter] = useState("");
  const [auditQuery, setAuditQuery] = useState("");
  const [auditPage, setAuditPage] = useState(0);
  const [auditClearing, setAuditClearing] = useState(false);
  const [auditExporting, setAuditExporting] = useState(false);
  // Date inputs are YYYY-MM-DDTHH:MM in local (Manila / GMT+8) time
  const [dateFromLocal, setDateFromLocal] = useState("");
  const [dateToLocal, setDateToLocal] = useState("");

  // Convert a "YYYY-MM-DDTHH:MM" Manila datetime into a UTC ISO timestamp.
  // The "to" bound is exclusive on the same minute by default (add a minute) so the
  // selected minute is included in results.
  const manilaDateTimeToUtcIso = (s, isEnd = false) => {
    if (!s) return undefined;
    // datetime-local gives "YYYY-MM-DDTHH:mm" (no seconds, no tz)
    const withSec = s.length === 16 ? `${s}:00` : s;
    const d = new Date(`${withSec}+08:00`);
    if (isEnd) {
      // Make inclusive: include the entire selected minute by adding 60s
      return new Date(d.getTime() + 60_000).toISOString();
    }
    return d.toISOString();
  };

  const loadAudit = async (opts = {}) => {
    const action = opts.action !== undefined ? opts.action : auditFilter;
    const q = opts.q !== undefined ? opts.q : auditQuery;
    const page = opts.page !== undefined ? opts.page : auditPage;
    const df = opts.dateFromLocal !== undefined ? opts.dateFromLocal : dateFromLocal;
    const dt = opts.dateToLocal !== undefined ? opts.dateToLocal : dateToLocal;
    setAuditLoading(true);
    try {
      const data = await fetchAuditLogs({
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        action: action || undefined,
        q: q || undefined,
        dateFrom: manilaDateTimeToUtcIso(df, false),
        dateTo: manilaDateTimeToUtcIso(dt, true),
      });
      setAuditLogs(data);
    } catch (e) {
      toast.error("Failed to load audit logs");
    } finally {
      setAuditLoading(false);
    }
  };

  const handleExportAudit = async () => {
    setAuditExporting(true);
    try {
      await downloadAuditExport({
        action: auditFilter || undefined,
        q: auditQuery || undefined,
        dateFrom: manilaDateTimeToUtcIso(dateFromLocal, false),
        dateTo: manilaDateTimeToUtcIso(dateToLocal, true),
      });
      toast.success("Audit log exported");
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setAuditExporting(false);
    }
  };

  const applyFilters = () => {
    setAuditPage(0);
    loadAudit({ page: 0 });
  };

  const clearAllFilters = () => {
    setAuditQuery("");
    setAuditFilter("");
    setDateFromLocal("");
    setDateToLocal("");
    setAuditPage(0);
    loadAudit({ q: "", action: "", dateFromLocal: "", dateToLocal: "", page: 0 });
  };

  const handleClearAudit = async (olderThanDays) => {
    const days = olderThanDays || 1;
    const msg = `Delete audit log entries older than ${days} day(s)? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    setAuditClearing(true);
    try {
      const res = await clearAuditLogs({ olderThanDays: days });
      toast.success(`Deleted ${res.deleted} audit entries`);
      setAuditPage(0);
      await loadAudit({ page: 0 });
    } catch (e) {
      toast.error("Failed to clear audit logs");
    } finally {
      setAuditClearing(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await me();
      } catch {
        navigate("/admin/login");
        return;
      }
      try {
        const [g, d] = await Promise.all([fetchGeneralSettings(), fetchDstSettings()]);
        setGeneralSettings(g);
        setTermOptionsInput((g.term_options || []).join(", "));
        setDst(d);
        await loadAudit({ action: "", q: "", page: 0 });
      } finally {
        setAuthChecking(false);
      }
    })();
  }, [navigate]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwd.next !== pwd.confirm) {
      toast.error("New password and confirmation do not match");
      return;
    }
    if (pwd.next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    setChangingPwd(true);
    try {
      await changePassword(pwd.current, pwd.next);
      toast.success("Password changed successfully");
      setPwd({ current: "", next: "", confirm: "" });
      await loadAudit();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to change password";
      toast.error(typeof msg === "string" ? msg : "Failed to change password");
    } finally {
      setChangingPwd(false);
    }
  };

  if (authChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  // --- General settings handlers ---
  const updateRatePct = (v) =>
    setGeneralSettings((s) => ({ ...s, default_monthly_rate: Number(v) / 100 }));

  const updateFormula = (v) =>
    setGeneralSettings((s) => ({ ...s, interest_formula: v }));

  const updatePmtRate = (idx, key, value) =>
    setGeneralSettings((s) => ({
      ...s,
      pmt_rates: s.pmt_rates.map((r, i) =>
        i === idx ? { ...r, [key]: Number(value) } : r
      ),
    }));

  const addPmtRate = () =>
    setGeneralSettings((s) => {
      const nextTerm = s.pmt_rates.length
        ? Math.max(...s.pmt_rates.map((r) => r.term_months)) + 1
        : 6;
      return { ...s, pmt_rates: [...s.pmt_rates, { term_months: nextTerm, annual_rate: 0 }] };
    });

  const removePmtRate = (idx) =>
    setGeneralSettings((s) => ({
      ...s,
      pmt_rates: s.pmt_rates.filter((_, i) => i !== idx),
    }));

  const updateVisibility = (k, v) =>
    setGeneralSettings((s) => ({
      ...s,
      field_visibility: { ...(s.field_visibility || {}), [k]: v },
    }));

  const saveGeneral = async () => {
    setSaving(true);
    try {
      const parsedTerms = termOptionsInput
        .split(",")
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => !isNaN(n) && n > 0);
      if (parsedTerms.length === 0) {
        toast.error("Please provide at least one valid term");
        return;
      }
      const pmt_rates = generalSettings.pmt_rates
        .filter((r) => !isNaN(Number(r.term_months)))
        .map((r) => ({
          term_months: Number(r.term_months),
          annual_rate: Number(r.annual_rate),
        }))
        .sort((a, b) => a.term_months - b.term_months);
      const payload = { ...generalSettings, term_options: parsedTerms, pmt_rates };
      const saved = await updateGeneralSettings(payload);
      setGeneralSettings(saved);
      setTermOptionsInput(saved.term_options.join(", "));
      toast.success("General settings saved");
      await loadAudit();
    } catch (e) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  // --- DST handlers ---
  const updateConst = (k, v) =>
    setDst((d) => ({ ...d, constants: { ...d.constants, [k]: Number(v) } }));

  const updateRule = (idx, key, value) =>
    setDst((d) => ({
      ...d,
      rules: d.rules.map((r, i) => (i === idx ? { ...r, [key]: value } : r)),
    }));

  const addRule = () =>
    setDst((d) => ({
      ...d,
      rules: [
        ...d.rules,
        {
          id: "r" + (d.rules.length + 1) + "-" + Date.now(),
          loan_for_business: "No",
          term_condition: "Less than 12 months",
          amount_condition: "Any amount",
          formula_type: "zero",
        },
      ],
    }));

  const removeRule = (idx) =>
    setDst((d) => ({ ...d, rules: d.rules.filter((_, i) => i !== idx) }));

  const saveDst = async () => {
    setSaving(true);
    try {
      await updateDst(dst);
      toast.success("DST settings saved");
      await loadAudit();
    } catch (e) {
      toast.error("Failed to save DST settings");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset all settings to defaults from the original Excel?")) return;
    setResetting(true);
    try {
      await resetDefaults();
      const [g, d] = await Promise.all([fetchGeneralSettings(), fetchDstSettings()]);
      setGeneralSettings(g);
      setTermOptionsInput((g.term_options || []).join(", "));
      setDst(d);
      toast.success("Defaults restored");
      await loadAudit();
    } catch (e) {
      toast.error("Reset failed");
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Topbar */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/logo.png" alt="Logo" className="h-9 w-auto object-contain" />
            <div>
              <h1 className="font-serif-display text-xl font-semibold text-slate-900 leading-tight">
                Admin Console
              </h1>
              <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500">
                Simulator Configuration
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/")}
              className="gap-2"
              data-testid="back-to-simulator-button"
            >
              <ArrowLeft className="h-4 w-4" /> Simulator
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              disabled={resetting}
              className="gap-2"
              data-testid="reset-defaults-button"
            >
              <RotateCcw className="h-4 w-4" /> Reset to Defaults
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="gap-2 text-slate-500"
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        {/* General Settings */}
        <Card data-testid="general-settings-card">
          <CardHeader className="flex flex-row items-start justify-between">
            <div>
              <CardTitle className="font-serif-display text-2xl">
                General Settings
              </CardTitle>
              <CardDescription>
                Default rate (per month), interest formula, and available loan terms for the simulator dropdown.
              </CardDescription>
            </div>
            <Button
              size="sm"
              onClick={saveGeneral}
              disabled={saving}
              className="gap-2 bg-slate-900 hover:bg-slate-800"
              data-testid="save-general-button"
            >
              <Save className="h-4 w-4" /> Save
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label className="text-[10px] tracking-[0.2em] uppercase text-slate-500">
                  Default Interest Rate (per month)
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="0.01"
                    value={(generalSettings.default_monthly_rate * 100).toFixed(2)}
                    onChange={(e) => updateRatePct(e.target.value)}
                    className="tabular-nums"
                    data-testid="default-rate-input"
                  />
                  <span className="text-slate-500 font-medium">%</span>
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <Switch
                    checked={!!generalSettings.allow_interest_rate_edit}
                    onCheckedChange={(v) =>
                      setGeneralSettings((s) => ({ ...s, allow_interest_rate_edit: v }))
                    }
                    data-testid="toggle-allow-rate-edit"
                  />
                  <Label className="text-xs text-slate-600 cursor-pointer">
                    Allow branch users to edit the interest rate
                  </Label>
                </div>
                <p className="text-xs text-slate-400">
                  When OFF, the rate is shown read-only on the simulator (Simple mode only — PMT mode always uses the lookup table).
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-[10px] tracking-[0.2em] uppercase text-slate-500">
                  Interest Formula
                </Label>
                <Select
                  value={generalSettings.interest_formula}
                  onValueChange={updateFormula}
                >
                  <SelectTrigger data-testid="interest-formula-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="simple">Simple / Add-on Interest</SelectItem>
                    <SelectItem value="pmt">PMT (Compound, Amortizing)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400">
                  Simple: <span className="font-mono">(P × (1 + r × n)) / n</span>
                  <br />
                  PMT: <span className="font-mono">r × P / (1 − (1+r)⁻ⁿ)</span>
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-[10px] tracking-[0.2em] uppercase text-slate-500">
                  Available Loan Terms (months)
                </Label>
                <Input
                  type="text"
                  value={termOptionsInput}
                  onChange={(e) => setTermOptionsInput(e.target.value)}
                  className="tabular-nums"
                  placeholder="6, 7, 8, 9, 10, 11, 12"
                  data-testid="term-options-input"
                />
                <p className="text-xs text-slate-400">
                  Comma-separated. These appear in the simulator dropdown.
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-[10px] tracking-[0.2em] uppercase text-slate-500">
                  Audit Log Retention (days)
                </Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={generalSettings.audit_retention_days ?? 90}
                  onChange={(e) =>
                    setGeneralSettings((s) => ({
                      ...s,
                      audit_retention_days: Math.max(0, parseInt(e.target.value || "0", 10)),
                    }))
                  }
                  className="tabular-nums"
                  data-testid="audit-retention-input"
                />
                <p className="text-xs text-slate-400">
                  Entries older than this are auto-purged. Set <b>0</b> to keep forever.
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-[10px] tracking-[0.2em] uppercase text-slate-500">
                  Minimum Loan Amount (PHP)
                </Label>
                <div className="flex items-center gap-2">
                  <span className="text-slate-500 font-medium">₱</span>
                  <Input
                    type="number"
                    min="0"
                    step="1000"
                    value={generalSettings.min_loan_amount ?? 200000}
                    onChange={(e) =>
                      setGeneralSettings((s) => ({
                        ...s,
                        min_loan_amount: Math.max(0, Number(e.target.value || 0)),
                      }))
                    }
                    className="tabular-nums"
                    data-testid="min-loan-amount-input"
                  />
                </div>
                <p className="text-xs text-slate-400">
                  Print &amp; Save-as-PDF are disabled on the simulator until the loan amount reaches this value.
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-[10px] tracking-[0.2em] uppercase text-slate-500">
                  Logo on Forms &amp; Prints
                </Label>
                <div className="flex items-center gap-2 pt-1">
                  <Switch
                    checked={generalSettings.show_logo !== false}
                    onCheckedChange={(v) =>
                      setGeneralSettings((s) => ({ ...s, show_logo: v }))
                    }
                    data-testid="toggle-show-logo"
                  />
                  <Label className="text-xs text-slate-600 cursor-pointer">
                    Show the logo image on the simulator and printouts
                  </Label>
                </div>
                <p className="text-xs text-slate-400">
                  Turn OFF for dot-matrix / impact printers that can&apos;t render images cleanly.
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-[10px] tracking-[0.2em] uppercase text-slate-500">
                  PNSA Page (Disclosure view)
                </Label>
                <div className="flex items-center gap-2 pt-1">
                  <Switch
                    checked={generalSettings.show_pnsa === true}
                    onCheckedChange={(v) =>
                      setGeneralSettings((s) => ({ ...s, show_pnsa: v }))
                    }
                    data-testid="toggle-show-pnsa"
                  />
                  <Label className="text-xs text-slate-600 cursor-pointer">
                    Attach the Promissory Note &amp; Security Agreement as page 2
                  </Label>
                </div>
                <p className="text-xs text-slate-400">
                  When ON, printing/Save-as-PDF from the Disclosure Statement view adds the PNSA on a second page.
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-[10px] tracking-[0.2em] uppercase text-slate-500">
                  Amortization Schedule (Disclosure view)
                </Label>
                <div className="flex items-center gap-2 pt-1">
                  <Switch
                    checked={generalSettings.show_amortization === true}
                    onCheckedChange={(v) =>
                      setGeneralSettings((s) => ({ ...s, show_amortization: v }))
                    }
                    data-testid="toggle-show-amortization"
                  />
                  <Label className="text-xs text-slate-600 cursor-pointer">
                    Attach the diminishing-balance amortization schedule as page 3
                  </Label>
                </div>
                <p className="text-xs text-slate-400">
                  When ON, printing/Save-as-PDF from the Disclosure Statement view adds the amortization schedule as an extra page.
                </p>
              </div>

              <div className="space-y-2 md:col-span-2 lg:col-span-3">
                <Label className="text-[10px] tracking-[0.2em] uppercase text-slate-500">
                  Simulator Theme
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    {
                      id: "modern",
                      title: "Modern",
                      blurb: "Editorial serif headings, soft layout, full color. Default look.",
                    },
                    {
                      id: "dot_matrix",
                      title: "Dot Matrix",
                      blurb: "Monospace, pure B/W, single-line borders. Optimised for 9/24-pin impact printers.",
                    },
                    {
                      id: "disclosure",
                      title: "Disclosure Statement",
                      blurb: "Truth-in-Lending (R.A. 3765) legal form: finance charges, EIR, conditional charges, signatures.",
                    },
                  ].map((opt) => {
                    const active = (generalSettings.theme || "modern") === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() =>
                          setGeneralSettings((s) => ({ ...s, theme: opt.id }))
                        }
                        data-testid={`theme-option-${opt.id}`}
                        className={`text-left rounded-md border p-3 transition-colors ${
                          active
                            ? "border-slate-900 bg-slate-900 text-white"
                            : "border-slate-200 hover:border-slate-400 bg-white text-slate-900"
                        }`}
                      >
                        <div
                          className={`text-sm font-semibold ${
                            opt.id === "dot_matrix" ? "font-mono tracking-wider uppercase" : ""
                          }`}
                        >
                          {opt.title}
                        </div>
                        <div
                          className={`text-xs mt-1 ${
                            active ? "text-slate-300" : "text-slate-500"
                          }`}
                        >
                          {opt.blurb}
                        </div>
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-400">
                  Theme applies to both the on-screen simulator and the printed/PDF output.
                </p>
              </div>
            </div>

            {/* Disclosure Statement Charges */}
            <div className="mt-8 pt-6 border-t border-slate-200" data-testid="disclosure-charges-section">
              <h3 className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold mb-1">
                Disclosure Statement — Conditional Charges
              </h3>
              <p className="text-xs text-slate-400 mb-3">
                Legal text shown in the Disclosure Statement view&apos;s conditional-charges table (R.A. 3765).
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { key: "missed_payment_penalty", label: "Missed Payment Penalty" },
                  { key: "prepayment_charges", label: "Prepayment Charges" },
                  { key: "past_due_interest", label: "Past Due Interest" },
                  { key: "past_due_penalty", label: "Past Due Penalty" },
                  { key: "attorneys_fees", label: "Attorney's Fees" },
                ].map((c) => (
                  <div key={c.key} className="space-y-1">
                    <Label className="text-[10px] tracking-[0.2em] uppercase text-slate-500">
                      {c.label}
                    </Label>
                    <Textarea
                      value={generalSettings.disclosure_charges?.[c.key] || ""}
                      onChange={(e) =>
                        setGeneralSettings((s) => ({
                          ...s,
                          disclosure_charges: { ...(s.disclosure_charges || {}), [c.key]: e.target.value },
                        }))
                      }
                      rows={3}
                      className="text-sm"
                      data-testid={`disclosure-charge-${c.key}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* PMT Rate Lookup Table */}
            <div className="mt-8 pt-6 border-t border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">
                    PMT Rate Lookup Table
                  </h3>
                  <p className="text-xs text-slate-400 mt-1">
                    Used only when <span className="font-mono">Interest Formula = PMT</span>. Annual rate is divided by 12 internally for the monthly period rate.
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addPmtRate}
                  className="gap-2"
                  data-testid="add-pmt-rate-button"
                >
                  <Plus className="h-4 w-4" /> Add Row
                </Button>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-40">Loan Term (months)</TableHead>
                    <TableHead className="w-56">Annual Rate (decimal)</TableHead>
                    <TableHead>As %</TableHead>
                    <TableHead className="w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(generalSettings.pmt_rates || []).map((r, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <Input
                          type="number"
                          value={r.term_months}
                          onChange={(e) => updatePmtRate(idx, "term_months", e.target.value)}
                          className="w-28"
                          data-testid={`pmt-rate-term-${idx}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.000001"
                          value={r.annual_rate}
                          onChange={(e) => updatePmtRate(idx, "annual_rate", e.target.value)}
                          className="w-44 tabular-nums"
                          data-testid={`pmt-rate-value-${idx}`}
                        />
                      </TableCell>
                      <TableCell className="tabular-nums text-slate-500">
                        {(Number(r.annual_rate) * 100).toFixed(4)}%
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removePmtRate(idx)}
                          data-testid={`remove-pmt-rate-${idx}`}
                        >
                          <Trash2 className="h-4 w-4 text-slate-400" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Optional Field Visibility */}
        <Card data-testid="field-visibility-card">
          <CardHeader className="flex flex-row items-start justify-between">
            <div>
              <CardTitle className="font-serif-display text-2xl">
                Optional Fields Visibility
              </CardTitle>
              <CardDescription>
                Fields not present in the original Excel A/B columns. Toggle each one ON to display it on the public simulator.
              </CardDescription>
            </div>
            <Button
              size="sm"
              onClick={saveGeneral}
              disabled={saving}
              className="gap-2 bg-slate-900 hover:bg-slate-800"
              data-testid="save-visibility-button"
            >
              <Save className="h-4 w-4" /> Save
            </Button>
          </CardHeader>
          <CardContent className="space-y-5">
            <VisibilityRow
              label="Loan for Business (Yes/No selector)"
              hint="Used internally for DST rule selection. If hidden, the default value below is used."
              checked={!!generalSettings.field_visibility?.show_loan_for_business}
              onChange={(v) => updateVisibility("show_loan_for_business", v)}
              testid="toggle-loan-for-business"
            />
            {!generalSettings.field_visibility?.show_loan_for_business && (
              <div className="ml-6 pl-4 border-l-2 border-slate-200 space-y-2 max-w-sm">
                <Label className="text-[10px] tracking-[0.2em] uppercase text-slate-500">
                  Default value when hidden
                </Label>
                <Select
                  value={generalSettings.field_visibility?.default_loan_for_business || "No"}
                  onValueChange={(v) => updateVisibility("default_loan_for_business", v)}
                >
                  <SelectTrigger data-testid="default-loan-for-business-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="No">No</SelectItem>
                    <SelectItem value="Yes">Yes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <VisibilityRow
              label="Number of Days (computed from loan term)"
              hint="Days between Date Granted and Maturity. Internal helper for the DST short-term formula."
              checked={!!generalSettings.field_visibility?.show_number_of_days}
              onChange={(v) => updateVisibility("show_number_of_days", v)}
              testid="toggle-number-of-days"
            />
            <VisibilityRow
              label="Maturity Date (Date Granted + term)"
              hint="Convenience field — not present in the original Excel."
              checked={!!generalSettings.field_visibility?.show_maturity_date}
              onChange={(v) => updateVisibility("show_maturity_date", v)}
              testid="toggle-maturity-date"
            />
          </CardContent>
        </Card>

        {/* DST */}
        {dst && (
          <Card data-testid="dst-card">
            <CardHeader className="flex flex-row items-start justify-between">
              <div>
                <CardTitle className="font-serif-display text-2xl">
                  Documentary Stamps Tax (DST)
                </CardTitle>
                <CardDescription>
                  Rules and constants used to compute DST on Promissory Note.
                </CardDescription>
              </div>
              <Button
                size="sm"
                onClick={saveDst}
                disabled={saving}
                className="gap-2 bg-slate-900 hover:bg-slate-800"
                data-testid="save-dst-button"
              >
                <Save className="h-4 w-4" /> Save DST
              </Button>
            </CardHeader>
            <CardContent className="space-y-8">
              {/* Constants */}
              <div>
                <h3 className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-3">
                  Formula Constants
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {[
                    ["short_term_factor", "Short-term Factor (e.g. 1.5)"],
                    ["short_term_divisor", "Short-term Divisor (e.g. 200)"],
                    ["short_term_days_basis", "Days Basis (e.g. 365)"],
                    ["long_term_rate", "Long-term Rate (e.g. 0.0075)"],
                    ["threshold_amount", "Threshold Amount (e.g. 250000)"],
                  ].map(([k, label]) => (
                    <div key={k} className="space-y-1">
                      <Label className="text-[10px] tracking-[0.2em] uppercase text-slate-500">
                        {label}
                      </Label>
                      <Input
                        type="number"
                        step="0.0001"
                        value={dst.constants[k]}
                        onChange={(e) => updateConst(k, e.target.value)}
                        className="tabular-nums"
                        data-testid={`dst-const-${k}`}
                      />
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-3 leading-relaxed">
                  <span className="font-semibold">Short-term formula:</span>{" "}
                  <span className="font-mono">(amount / divisor × factor) × days / days_basis</span>{" "}
                  &nbsp;·&nbsp;
                  <span className="font-semibold">Long-term formula:</span>{" "}
                  <span className="font-mono">amount × rate</span>
                </p>
              </div>

              {/* Rules table */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-[10px] tracking-[0.2em] uppercase text-slate-500">
                    Rules (first match wins)
                  </h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addRule}
                    className="gap-2"
                    data-testid="add-dst-rule-button"
                  >
                    <Plus className="h-4 w-4" /> Add Rule
                  </Button>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Loan for Business</TableHead>
                      <TableHead>Loan Term</TableHead>
                      <TableHead>Amount Range</TableHead>
                      <TableHead>Formula</TableHead>
                      <TableHead className="w-16"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dst.rules.map((r, idx) => (
                      <TableRow key={r.id}>
                        <TableCell>
                          <RuleSelect
                            value={r.loan_for_business}
                            options={["Yes", "No"]}
                            onChange={(v) => updateRule(idx, "loan_for_business", v)}
                            testid={`dst-business-${idx}`}
                          />
                        </TableCell>
                        <TableCell>
                          <RuleSelect
                            value={r.term_condition}
                            options={["Less than 12 months", "Above 12 months"]}
                            onChange={(v) => updateRule(idx, "term_condition", v)}
                            testid={`dst-term-${idx}`}
                          />
                        </TableCell>
                        <TableCell>
                          <RuleSelect
                            value={r.amount_condition}
                            options={[
                              "Any amount",
                              "Above 250,000.00",
                              "250,000.00 and Below",
                            ]}
                            onChange={(v) => updateRule(idx, "amount_condition", v)}
                            testid={`dst-amount-${idx}`}
                          />
                        </TableCell>
                        <TableCell>
                          <RuleSelect
                            value={r.formula_type}
                            options={["short_term", "long_term", "zero"]}
                            onChange={(v) => updateRule(idx, "formula_type", v)}
                            testid={`dst-formula-${idx}`}
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeRule(idx)}
                            data-testid={`remove-dst-rule-${idx}`}
                          >
                            <Trash2 className="h-4 w-4 text-slate-400" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Account Security */}
        <Card data-testid="account-security-card">
          <CardHeader>
            <CardTitle className="font-serif-display text-2xl flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-slate-700" />
              Account Security
            </CardTitle>
            <CardDescription>
              Change the admin account password. Minimum 8 characters.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={handleChangePassword}
              className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl"
              data-testid="change-password-form"
            >
              <div className="space-y-1">
                <Label className="text-[10px] tracking-[0.2em] uppercase text-slate-500">
                  Current Password
                </Label>
                <Input
                  type="password"
                  autoComplete="current-password"
                  value={pwd.current}
                  onChange={(e) => setPwd((p) => ({ ...p, current: e.target.value }))}
                  required
                  data-testid="current-password-input"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] tracking-[0.2em] uppercase text-slate-500">
                  New Password
                </Label>
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={pwd.next}
                  onChange={(e) => setPwd((p) => ({ ...p, next: e.target.value }))}
                  required
                  data-testid="new-password-input"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] tracking-[0.2em] uppercase text-slate-500">
                  Confirm New Password
                </Label>
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={pwd.confirm}
                  onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))}
                  required
                  data-testid="confirm-password-input"
                />
              </div>
              <div className="md:col-span-3 flex items-center gap-3 pt-2">
                <Button
                  type="submit"
                  disabled={changingPwd}
                  className="gap-2 bg-slate-900 hover:bg-slate-800"
                  data-testid="change-password-button"
                >
                  {changingPwd ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                  Change Password
                </Button>
                <p className="text-xs text-slate-500">
                  Your existing session stays valid; next login will require the new password.
                </p>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Audit Log */}
        <Card data-testid="audit-log-card">
          <CardHeader>
            <div className="flex flex-col gap-3">
              <div className="flex flex-row items-start justify-between gap-4">
                <div>
                  <CardTitle className="font-serif-display text-2xl flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5 text-slate-700" />
                    Audit Log
                  </CardTitle>
                  <CardDescription>
                    Paginated, searchable trail of all admin actions. Entries older than the retention period are auto-purged.
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExportAudit}
                    disabled={auditExporting}
                    className="gap-2"
                    data-testid="export-audit-button"
                  >
                    {auditExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    Export CSV
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleClearAudit(30)}
                    disabled={auditClearing}
                    className="gap-2"
                    data-testid="clear-old-button"
                  >
                    <Eraser className="h-4 w-4" /> Clear &gt; 30 days
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleClearAudit(1)}
                    disabled={auditClearing}
                    className="gap-2 text-rose-700 hover:text-rose-800 border-rose-200 hover:bg-rose-50"
                    data-testid="clear-all-button"
                  >
                    <Trash2 className="h-4 w-4" /> Clear &gt; 1 day
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => loadAudit()}
                    className="gap-2"
                    disabled={auditLoading}
                    data-testid="refresh-audit-button"
                  >
                    <RefreshCw className={`h-4 w-4 ${auditLoading ? "animate-spin" : ""}`} />
                    Refresh
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                <div className="md:col-span-5 relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Search actor, action, resource, IP…"
                    value={auditQuery}
                    onChange={(e) => setAuditQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") applyFilters();
                    }}
                    className="pl-8"
                    data-testid="audit-search-input"
                  />
                </div>
                <div className="md:col-span-2 relative">
                  <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none z-10" />
                  <Input
                    type="datetime-local"
                    value={dateFromLocal}
                    onChange={(e) => setDateFromLocal(e.target.value)}
                    title="From date & time (GMT+8)"
                    className="pl-7 tabular-nums text-xs"
                    data-testid="audit-date-from"
                  />
                </div>
                <div className="md:col-span-2 relative">
                  <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none z-10" />
                  <Input
                    type="datetime-local"
                    value={dateToLocal}
                    onChange={(e) => setDateToLocal(e.target.value)}
                    title="To date & time (GMT+8)"
                    className="pl-7 tabular-nums text-xs"
                    data-testid="audit-date-to"
                  />
                </div>
                <div className="md:col-span-3">
                  <Select
                    value={auditFilter || "all"}
                    onValueChange={(v) => {
                      const val = v === "all" ? "" : v;
                      setAuditFilter(val);
                      setAuditPage(0);
                      loadAudit({ action: val, page: 0 });
                    }}
                  >
                    <SelectTrigger data-testid="audit-filter">
                      <SelectValue placeholder="All actions" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All actions</SelectItem>
                      <SelectItem value="login_success">login_success</SelectItem>
                      <SelectItem value="login_failed">login_failed</SelectItem>
                      <SelectItem value="update_general_settings">update_general_settings</SelectItem>
                      <SelectItem value="update_dst_settings">update_dst_settings</SelectItem>
                      <SelectItem value="reset_defaults">reset_defaults</SelectItem>
                      <SelectItem value="change_password">change_password</SelectItem>
                      <SelectItem value="clear_audit_logs">clear_audit_logs</SelectItem>
                      <SelectItem value="export_audit_logs">export_audit_logs</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={applyFilters}
                  data-testid="audit-apply-filters"
                >
                  Apply Filters
                </Button>
                {(auditQuery || dateFromLocal || dateToLocal || auditFilter) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearAllFilters}
                    data-testid="audit-clear-filters"
                  >
                    Clear Filters
                  </Button>
                )}
                <p className="text-xs text-slate-400 ml-auto">
                  Date & time are in <b>GMT+8</b> (Philippines time).
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-48">Timestamp (GMT+8)</TableHead>
                  <TableHead className="w-44">Actor</TableHead>
                  <TableHead className="w-52">Action</TableHead>
                  <TableHead className="w-20 text-center">Status</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead className="w-32">IP</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {auditLogs.items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-slate-400 py-6">
                      {auditLoading ? "Loading..." : "No audit entries match."}
                    </TableCell>
                  </TableRow>
                ) : (
                  auditLogs.items.map((log) => (
                    <TableRow key={log.id} data-testid={`audit-row-${log.id}`}>
                      <TableCell className="font-mono text-xs text-slate-600">
                        {formatManilaTime(log.timestamp)}
                      </TableCell>
                      <TableCell className="text-sm">{log.actor_email}</TableCell>
                      <TableCell>
                        <span className="font-mono text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                          {log.action}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span
                          className={`inline-block text-[10px] tracking-wide uppercase px-2 py-0.5 rounded font-medium ${
                            log.success
                              ? "bg-emerald-50 text-emerald-700"
                              : "bg-rose-50 text-rose-700"
                          }`}
                        >
                          {log.success ? "OK" : "Fail"}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs text-slate-600">
                        {log.resource && (
                          <div className="font-mono text-slate-500 mb-1">{log.resource}</div>
                        )}
                        <AuditDetails details={log.details} />
                      </TableCell>
                      <TableCell className="font-mono text-xs text-slate-500">
                        {log.ip || "—"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            {/* Pagination */}
            <div className="flex items-center justify-between mt-4 text-xs text-slate-500">
              <div data-testid="audit-pagination-info">
                {auditLogs.total === 0
                  ? "0 entries"
                  : `Showing ${auditPage * PAGE_SIZE + 1}–${Math.min((auditPage + 1) * PAGE_SIZE, auditLogs.total)} of ${auditLogs.total}`}
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={auditPage === 0 || auditLoading}
                  onClick={() => { const p = auditPage - 1; setAuditPage(p); loadAudit({ page: p }); }}
                  className="gap-1"
                  data-testid="audit-prev-page"
                >
                  <ChevronLeft className="h-4 w-4" /> Prev
                </Button>
                <span className="font-mono text-slate-600 px-2">
                  Page {auditPage + 1} of {Math.max(1, Math.ceil(auditLogs.total / PAGE_SIZE))}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={(auditPage + 1) * PAGE_SIZE >= auditLogs.total || auditLoading}
                  onClick={() => { const p = auditPage + 1; setAuditPage(p); loadAudit({ page: p }); }}
                  className="gap-1"
                  data-testid="audit-next-page"
                >
                  Next <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

function RuleSelect({ value, options, onChange, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-full" data-testid={testid}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o} value={o}>
            {o}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function VisibilityRow({ label, hint, checked, onChange, testid }) {
  return (
    <div className="flex items-start justify-between gap-6 py-2">
      <div className="flex-1">
        <p className="text-sm font-medium text-slate-900">{label}</p>
        <p className="text-xs text-slate-500 mt-0.5">{hint}</p>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onChange}
        data-testid={testid}
      />
    </div>
  );
}

// Format an ISO UTC timestamp into "YYYY-MM-DD HH:mm:ss" in Asia/Manila (GMT+8).
function formatManilaTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Manila",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(d);
    const get = (t) => parts.find((p) => p.type === t)?.value || "";
    return `${get("year")}-${get("month")}-${get("day")} ${get("hour")}:${get("minute")}:${get("second")}`;
  } catch {
    return String(iso).replace("T", " ").slice(0, 19);
  }
}

// Pretty-print a from/to value for the audit log table.
function formatAuditValue(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "true" : "false";
  if (typeof v === "number") return String(v);
  if (typeof v === "string") return v;
  if (Array.isArray(v)) {
    if (v.length === 0) return "[]";
    // For short arrays of primitives, render inline
    const allPrim = v.every((x) => typeof x !== "object" || x === null);
    if (allPrim && v.length <= 10) return `[${v.join(", ")}]`;
    return JSON.stringify(v);
  }
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function ChangeList({ changes }) {
  const entries = Object.entries(changes || {});
  if (entries.length === 0) return null;
  return (
    <ul className="space-y-1 mt-1">
      {entries.map(([key, { from, to }]) => (
        <li key={key} className="font-mono">
          <span className="text-slate-700 font-semibold">{key}:</span>{" "}
          <span className="text-rose-600">{formatAuditValue(from)}</span>
          <span className="mx-1 text-slate-400">→</span>
          <span className="text-emerald-700">{formatAuditValue(to)}</span>
        </li>
      ))}
    </ul>
  );
}

function AuditDetails({ details }) {
  if (!details || Object.keys(details).length === 0) return null;
  // update_general_settings / update_dst_settings → details.changes
  if (details.changes) {
    return <ChangeList changes={details.changes} />;
  }
  // reset_defaults → details.general_changes + details.dst_changes
  if (details.general_changes || details.dst_changes) {
    return (
      <div className="space-y-2">
        {details.general_changes && Object.keys(details.general_changes).length > 0 && (
          <div>
            <div className="text-[10px] tracking-wider uppercase text-slate-500">General</div>
            <ChangeList changes={details.general_changes} />
          </div>
        )}
        {details.dst_changes && Object.keys(details.dst_changes).length > 0 && (
          <div>
            <div className="text-[10px] tracking-wider uppercase text-slate-500">DST</div>
            <ChangeList changes={details.dst_changes} />
          </div>
        )}
      </div>
    );
  }
  // Fallback — render JSON for anything else
  return (
    <div className="font-mono text-slate-500 break-all">
      {JSON.stringify(details)}
    </div>
  );
}
